import { useEffect, useState } from "react";
import { AlertTriangle, Ban, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { HistoryList } from "@/components/calendar/HistoryList";
import { fmtDateTime } from "@/components/ticketMeta";
import { APPT_STATUSES, dateKey } from "@/lib/cal";
import { api, formatApiError } from "@/lib/api";

const blank = (preset) => ({
  staff_id: preset?.staff_id || "", service_id: "", date: preset?.start?.slice(0, 10) || dateKey(new Date()),
  time: preset?.start?.slice(11, 16) || "09:00", duration: "", client_name: "", client_phone: "", address: "", notes: "", status: "programat",
});

const fromAppt = (a) => ({
  staff_id: a.staff_id, service_id: a.service_id, date: a.start.slice(0, 10), time: a.start.slice(11, 16), duration: a.duration,
  client_name: a.client_name, client_phone: a.client_phone, address: a.address, notes: a.notes, status: a.status,
});

const F = ({ label, className, children }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Pick = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Warnings = ({ list, onForce, saving }) => (
  <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="appt-conflict-warnings">
    <div className="flex items-center gap-2 font-semibold text-amber-600 dark:text-amber-400"><AlertTriangle className="h-4 w-4" /> Atenție la program</div>
    <ul className="list-disc space-y-0.5 pl-5">{list.map((w, i) => <li key={i}>{w}</li>)}</ul>
    <Button size="sm" variant="outline" onClick={onForce} disabled={saving} className="border-amber-500/50" data-testid="appt-force-save-button">Salvează oricum</Button>
  </div>
);

const ApptFields = ({ f, set, setF, staff, services, isEdit }) => {
  const pickService = (id) => setF({ ...f, service_id: id, duration: services.find((s) => s.id === id)?.duration ?? f.duration });
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <F label="Coleg *"><Pick testId="appt-staff-select" value={f.staff_id} onChange={set("staff_id")} placeholder="Alege colegul" options={staff.map((s) => ({ value: s.id, label: s.name }))} /></F>
      <F label="Serviciu *"><Pick testId="appt-service-select" value={f.service_id} onChange={pickService} placeholder="Alege serviciul" options={services.map((s) => ({ value: s.id, label: `${s.name} (${s.duration} min)` }))} /></F>
      <F label="Data"><Input type="date" value={f.date} onChange={set("date")} data-testid="appt-date-input" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Ora"><Input type="time" step="900" value={f.time} onChange={set("time")} data-testid="appt-time-input" /></F>
        <F label="Durată (min)"><Input type="number" min="5" step="5" value={f.duration} onChange={set("duration")} data-testid="appt-duration-input" /></F>
      </div>
      <F label="Client"><Input value={f.client_name} onChange={set("client_name")} placeholder="opțional" data-testid="appt-client-input" /></F>
      <F label="Telefon"><Input value={f.client_phone} onChange={set("client_phone")} placeholder="opțional" data-testid="appt-phone-input" /></F>
      <F label="Adresă" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} placeholder="opțional" data-testid="appt-address-input" /></F>
      <F label="Note" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="appt-notes-input" /></F>
      {isEdit && <F label="Status"><Pick testId="appt-status-select" value={f.status} onChange={set("status")} options={APPT_STATUSES} /></F>}
    </div>
  );
};

export const AppointmentDialog = ({ open, onOpenChange, appt, preset, staff, services, onSaved, isAdmin }) => {
  const [f, setF] = useState(blank());
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setF(appt ? fromAppt(appt) : blank(preset)); setWarnings([]); }
  }, [open, appt, preset]);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const send = async (payload, msg) => {
    setSaving(true);
    try {
      if (appt) await api.put(`/appointments/${appt.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(msg);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 409) setWarnings(e.response.data.detail?.warnings || []);
      else toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const submit = (force = false) => {
    if (!f.staff_id || !f.service_id || !f.date || !f.time) { toast.error("Alege colegul, serviciul, data și ora"); return; }
    const { date, time, duration, status, ...rest } = f;
    const payload = { ...rest, start: `${date}T${time}`, duration: Number(duration) || undefined, force };
    if (appt) payload.status = status;
    send(payload, appt ? "Programare actualizată" : "Programare creată");
  };

  const cancelAppt = () => send({ status: "anulat" }, "Programare anulată");

  const remove = async () => {
    if (!window.confirm("Ștergi definitiv programarea?")) return;
    try { await api.delete(`/appointments/${appt.id}`); toast.success("Programare ștearsă"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const form = (
    <div className="space-y-4">
      <ApptFields f={f} set={set} setF={setF} staff={staff} services={services} isEdit={!!appt} />
      {warnings.length > 0 && <Warnings list={warnings} onForce={() => submit(true)} saving={saving} />}
      <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-between">
        <div className="flex gap-2">
          {appt && appt.status !== "anulat" && <Button variant="ghost" onClick={cancelAppt} disabled={saving} data-testid="appt-cancel-button"><Ban className="h-4 w-4" /> Anulează programarea</Button>}
          {appt && isAdmin && <Button variant="ghost" onClick={remove} className="text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="appt-delete-button"><Trash2 className="h-4 w-4" /></Button>}
        </div>
        <Button onClick={() => submit(false)} disabled={saving} className="font-semibold" data-testid="appt-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle>{appt ? "Programare" : "Programare nouă"}</DialogTitle>
          {appt && (
            <p className="text-xs text-muted-foreground" data-testid="appt-audit-info">
              Creată de <b>{appt.created_by}</b> · {fmtDateTime(appt.created_at)}
              {appt.history?.length > 1 && <> · Ultima modificare: <b>{appt.updated_by}</b> · {fmtDateTime(appt.updated_at)}</>}
            </p>
          )}
        </DialogHeader>
        {appt ? (
          <Tabs defaultValue="detalii">
            <TabsList className="mb-4">
              <TabsTrigger value="detalii" data-testid="appt-tab-details">Detalii</TabsTrigger>
              <TabsTrigger value="istoric" data-testid="appt-tab-history">Istoric ({appt.history?.length || 0})</TabsTrigger>
            </TabsList>
            <TabsContent value="detalii">{form}</TabsContent>
            <TabsContent value="istoric"><HistoryList history={appt.history} /></TabsContent>
          </Tabs>
        ) : form}
      </DialogContent>
    </Dialog>
  );
};
